import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useRef } from "react";

import { AssetGenerationTile } from "@/components/Tiles/AssetGenerationTile/AssetGenerationTile";
import {
  AudioGenerationTile,
  AudioGenerationTileProps,
} from "@/components/Tiles/AudioGenerationTile/AudioGenerationTile";
import { ImageGenerationTile } from "@/components/Tiles/ImageGenerationTile/ImageGenerationTile";
import { MeshGenerationTile } from "@/components/Tiles/MeshGenerationTile/MeshGenerationTile";
import {
  VideoGenerationTile,
  VideoGenerationTileProps,
} from "@/components/Tiles/VideoGenerationTile/VideoGenerationTile";
import { classes as selectableClasses } from "@/components/ui-v2/Selectable/Selectable";
import { classes as tileBaseClasses } from "@/components/ui-v2/Tiles/TileBase/TileBase";
import {
  SessionAssetFragment,
  SessionFileFragment,
} from "@/features/forge/forge-session/queries.graphql";
import { ObjectType } from "@/types";
import { getObjectType } from "@/utils/libraryObjectUtils";

import type { CommonGenerationTileProps, GenerationTileAction } from "./types";

// Props used only by GenerationTile.
interface GenerationTileInternalProps {
  object: SessionFileFragment | SessionAssetFragment;
  isFocused?: boolean;
  isButtonsSectionHidden?: boolean;
  isObjectTypeIndicatorHidden?: boolean;
  isLikeHidden?: boolean;
  isControlsSectionHidden?: boolean;
  onAction?: (
    event: React.MouseEvent,
    action: GenerationTileAction,
    id: string,
  ) => void;
}

// Props used by GenerationTile.
interface GenerationTileProps
  extends GenerationTileInternalProps,
    Partial<
      Omit<
        CommonGenerationTileProps<SessionFileFragment | SessionAssetFragment>,
        "onAction" | "object"
      >
    >,
    Partial<Omit<VideoGenerationTileProps, "onAction" | "object">>,
    Partial<Omit<AudioGenerationTileProps, "onAction" | "object">> {
  videoGenerationTileProps?: Partial<VideoGenerationTileProps>;
  audioGenerationTileProps?: Partial<AudioGenerationTileProps>;
}

const Root = styled("div", {
  shouldForwardProp: (prop: string) =>
    ![
      "isSelected",
      "isFocused",
      "isLiked",
      "isLiking",
      "isButtonsSectionHidden",
      "isObjectTypeIndicatorHidden",
      "isLikeHidden",
      "isControlsSectionHidden",
    ].includes(prop),
})<{
  isSelected: boolean;
  isFocused: boolean;
  isLiked: boolean;
  isLiking: boolean;
  isButtonsSectionHidden: boolean;
  isObjectTypeIndicatorHidden: boolean;
  isLikeHidden: boolean;
  isControlsSectionHidden: boolean;
}>(
  ({
    isSelected,
    isFocused,
    isLiked,
    isLiking,
    isButtonsSectionHidden,
    isObjectTypeIndicatorHidden,
    isLikeHidden,
    isControlsSectionHidden,
    theme,
  }) => ({
    [`& .${tileBaseClasses.bottomLeftEl}`]: {
      transition: "all 0.1s ease-in-out",
      display: isButtonsSectionHidden ? "none" : "block",
      opacity: isSelected || isFocused ? 1 : 0,
    },
    [`& .${tileBaseClasses.bottomRightEl}`]: {
      transition: "all 0.1s ease-in-out",
      display: isLikeHidden ? "none" : "block",
      opacity: isLiked || isLiking || isSelected || isFocused ? 1 : 0,
    },
    [`& .${tileBaseClasses.topLeftEl}`]: {
      transition: "all 0.1s ease-in-out",
      display: isObjectTypeIndicatorHidden ? "none" : "block",
    },
    [`& .${tileBaseClasses.topRightEl}`]: {
      transition: "all 0.1s ease-in-out",
      display: isControlsSectionHidden ? "none" : "block",
      opacity: isSelected || isFocused ? 1 : 0,
    },
    [`& .${selectableClasses.root}:focus-within, &:hover`]: {
      [`& .${tileBaseClasses.bottomLeftEl}`]: {
        opacity: 1,
      },
      [`& .${tileBaseClasses.bottomRightEl}`]: {
        opacity: 1,
      },
      [`& .${tileBaseClasses.topRightEl}`]: {
        opacity: 1,
      },
      [`& .${tileBaseClasses.topLeftEl}`]: {
        backgroundColor: isSelected
          ? theme.palette.primary.light
          : theme.palette.secondary[4],
      },
    },
  }),
);

const TILE_COMPONENTS_MAP: Partial<
  Record<ObjectType | "asset", React.ComponentType<any>>
> = {
  asset: AssetGenerationTile,
  image: ImageGenerationTile,
  video: VideoGenerationTile,
  mesh: MeshGenerationTile,
  audio: AudioGenerationTile,
};

export const GenerationTile = (props: GenerationTileProps) => {
  const {
    object,
    isSelected = false,
    isFocused = false,
    isLiking = false,
    isButtonsSectionHidden = false,
    isObjectTypeIndicatorHidden = false,
    isLikeHidden = false,
    isControlsSectionHidden = false,
    videoGenerationTileProps = {},
    audioGenerationTileProps = {},
    onAction,
    ...rest
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const isLiked = object.isLiked ?? false;
  const isAsset = object.__typename === "Asset";
  const objectType = isAsset ? "asset" : getObjectType(object);

  const handleAction = useCallback(
    (e: React.MouseEvent, action: GenerationTileAction) => {
      onAction?.(e, action, object.id);
    },
    [onAction, object.id],
  );

  useEffect(() => {
    if (isSelected && ref.current) {
      const selectable = ref.current.querySelector(
        `.${selectableClasses.root}`,
      );
      if (selectable) {
        (selectable as HTMLDivElement).focus();
      }
    }
  }, [isSelected]);

  const TileComponent = TILE_COMPONENTS_MAP[objectType];

  if (!TileComponent) {
    return null;
  }

  return (
    <Root
      ref={ref}
      data-testid="generation-tile"
      data-object-id={object.id}
      data-object-type={objectType}
      data-selected={isSelected}
      data-focused={isFocused}
      data-isliked={isLiked}
      isSelected={isSelected}
      isFocused={isFocused}
      isLiked={isLiked}
      isLiking={isLiking}
      isButtonsSectionHidden={isButtonsSectionHidden}
      isObjectTypeIndicatorHidden={isObjectTypeIndicatorHidden}
      isLikeHidden={isLikeHidden}
      isControlsSectionHidden={isControlsSectionHidden}
    >
      <TileComponent
        isSelected={isSelected}
        isFocused={isFocused}
        isLiking={isLiking}
        object={object}
        onAction={handleAction}
        {...(objectType === "video" && videoGenerationTileProps)}
        {...(objectType === "audio" && audioGenerationTileProps)}
        {...rest}
      />
    </Root>
  );
};
